'use client'

import { useEffect, useRef, useState } from 'react'
import {
  FaClock,
  FaExpandAlt,
  FaGem,
  FaMicrochip,
  FaMicrophoneAlt,
  FaRunning,
  FaTachometerAlt,
  FaTimes,
  FaVideo,
} from 'react-icons/fa'
import { HiSpeakerphone } from 'react-icons/hi'
import { useTranslation } from 'react-i18next'
import VoiceSettings from './VoiceSettings'

import Portal from './Portal'
import { useAuth } from '../contexts/AuthContext'
import {
  isUrl,
  aspectFromRatio,
  aspectFromImageSrc,
  getVideoGemCost,
} from '../helpers/helpers'
import {
  VIDEO_V4,
  VIDEO_V5,
  VIDEO_MODEL_AUTO,
  resolveVideoVersion,
  isAutoVoice,
  isLtx,
  LTX_CAMERA_MOVES,
  LTX_DIALOGUE_SPEAKER_DEFAULT,
  LTX_DIALOGUE_ACCENT_DEFAULT,
  LTX_DIALOGUE_TONE_DEFAULT,
  LTX_LENGTH_DEFAULT,
  LTX_RESOLUTION_DEFAULT,
  LTX_FPS_DEFAULT,
  LTX_LENGTHS,
  LTX_FPS_OPTIONS,
  LTX_RESOLUTIONS,
  normalizeResolution,
  dialogueWordCount,
  ltxDialogueLimits,
  normalizeAspectForVersion,
} from '../helpers/videoVersions'

// Segmented single-choice control (mirrors the button-pair pattern used across
// InputsVideo for Motion / Image Size / Quality).
function Segmented({ options, value, onChange }) {
  return (
    <div className="flex flex-1 gap-1">
      {options.map((o) => (
        <button
          key={o.value || 'default'}
          type="button"
          onClick={() => onChange(o.value)}
          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
            value === o.value
              ? 'bg-white text-black'
              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// Image-to-video "Animate" modal. Pure controlled component: it collects the user's
// motion / orientation / quality / audio choices and hands them to `onConfirm`. It
// NEVER fires generation itself — the caller (Create.jsx on /generate) owns
// handleSubmitVideoV3. Orientation defaults to the source image's aspect ratio, which
// is what fixes the "always wide" bug (the old one-tap path never set it).
export default function AnimateModal({
  isOpen,
  closeModal,
  // { url, isUrl, id, width?, height?, model?, scene? (a story's New scene: no url when it is described, plus the
  // story's orientation and, for a Try again, its words in `prompt`) }
  source,
  videoModelMode = VIDEO_MODEL_AUTO,
  // v5 settings the modal both SHOWS and EDITS, seeded from the caller's persisted values.
  // They are editable here for parity with the Video tab: this modal is a full alternative entry
  // point, so animating from it must not silently inherit settings the user cannot see. All three
  // also feed the gem total, and the clip length additionally drives the dialogue word budget
  // (it is a duration budget, so a 10s clip allows a longer line than a 6s one).
  videoLength: videoLengthInitial = LTX_LENGTH_DEFAULT,
  videoResolution: videoResolutionInitial = LTX_RESOLUTION_DEFAULT,
  videoFps: videoFpsInitial = LTX_FPS_DEFAULT,
  initial, // last-used v5 settings from CreateContext — see the reset effect below
  onConfirm,
}) {
  const { t } = useTranslation()
  const { safe } = useAuth()

  const [shouldRender, setShouldRender] = useState(isOpen)
  // The MODE is the state; the version is derived from it and this modal's own dialogue draft
  // (below), so typing a line here selects the voice model exactly as it does on the Video tab.
  const [modelMode, setModelMode] = useState(videoModelMode)
  const [animateMode, setAnimateMode] = useState('auto') // 'auto' | 'prompt'
  const [motionPrompt, setMotionPrompt] = useState('')
  const [orientation, setOrientation] = useState('Portrait') // Square | Portrait | Wide
  const [videoQuality, setVideoQuality] = useState('High') // High is intended default
  const [audioEnabled, setAudioEnabled] = useState(false)
  // v5 only. Seeded from the caller's persisted settings on open — see the reset effect.
  const [dialogue, setDialogue] = useState('')
  const [dialogueSpeaker, setDialogueSpeaker] = useState(
    LTX_DIALOGUE_SPEAKER_DEFAULT,
  )
  const [dialogueTone, setDialogueTone] = useState(LTX_DIALOGUE_TONE_DEFAULT)
  const [dialogueAccent, setDialogueAccent] = useState(
    LTX_DIALOGUE_ACCENT_DEFAULT,
  )
  const [cameraMove, setCameraMove] = useState('')
  // Clip length / frame rate / resolution are real controls here, not just pricing inputs — the
  // modal is a full alternative to the Video tab, so it must offer the same v5 settings. Seeded
  // from the caller's persisted values on open, same rule as dialogue and camera above.
  const [videoLength, setVideoLength] = useState(videoLengthInitial)
  const [videoFps, setVideoFps] = useState(videoFpsInitial)
  const [videoResolution, setVideoResolution] = useState(videoResolutionInitial)

  // Held in a ref, not in the effect deps: the draft must be seeded on OPEN, not whenever a
  // default changes underneath an already-open modal — that would wipe a half-typed line.
  const initialRef = useRef(initial)
  initialRef.current = initial

  // Derived, never stored — same rule as CreateContext. Declared after `dialogue` because Auto
  // reads it.
  const version = resolveVideoVersion(modelMode, dialogue)
  const ltx = isLtx(version)
  // Camera motion selects a lora, and safe mode zeroes every content lora — so it is hidden
  // there. Dialogue is not a lora and stays available: safe mode locks the scene, not the
  // soundtrack. Same split as the Video tab.
  const ltxExtras = ltx && safe !== true
  // A story's New scene: described in words when there's no picture, and always in the story's shape, so it joins it.
  const describing = !!source?.scene && !source?.url
  const storyShape = source?.scene?.orientation
  // v4-voice makes no square video, so a square story's scene can't use it.
  const shapeClash =
    !!storyShape &&
    normalizeAspectForVersion(storyShape, version) !== storyShape

  const pickVideoModel = (nextMode) => {
    if (nextMode === modelMode) return
    setModelMode(nextMode)
  }

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'
  }, [isOpen])

  // Reset the draft each time the modal opens, and derive the default orientation
  // from the source. Prefer the known dimensions (synchronous); fall back to probing
  // the image only when dims aren't provided (e.g. a session-feed item).
  useEffect(() => {
    if (!isOpen) return
    setShouldRender(true)
    setModelMode(videoModelMode)
    setVideoQuality('High')
    setAudioEnabled(false)
    // v5 settings follow the same "remember the last one used" rule as the Video tab, so the
    // modal opens on the caller's persisted values rather than blank. Without this, opening
    // Animate and confirming would push empty defaults back into context and silently clear
    // what the user had set, because onConfirm writes them all back.
    const last = initialRef.current || {}
    // Motion now follows that SAME rule. It used to be hard-reset to auto/'' on every open
    // while dialogue was restored — so reopening Animate silently dropped a motion line and
    // kept a spoken one, which is the asymmetry that ends in the character reciting the motion
    // description. onConfirm writes both back, so both must be seeded the same way.
    setAnimateMode(
      describing || last.animateMode === 'prompt' ? 'prompt' : 'auto',
    )
    setMotionPrompt(describing ? source.prompt || '' : last.motionPrompt || '')
    setDialogue(last.dialogue || '')
    setDialogueSpeaker(last.dialogueSpeaker || LTX_DIALOGUE_SPEAKER_DEFAULT)
    setDialogueTone(last.dialogueTone || LTX_DIALOGUE_TONE_DEFAULT)
    setDialogueAccent(last.dialogueAccent || LTX_DIALOGUE_ACCENT_DEFAULT)
    setCameraMove(last.cameraMove || '')
    setVideoLength(videoLengthInitial)
    setVideoFps(videoFpsInitial)
    setVideoResolution(normalizeResolution(videoResolutionInitial))

    const sync = storyShape || aspectFromRatio(source?.width, source?.height)
    if (sync) {
      setOrientation(sync)
      return
    }
    let alive = true
    if (source?.url) {
      aspectFromImageSrc(source.url).then((a) => {
        if (alive) setOrientation(a)
      })
    } else {
      setOrientation('Portrait')
    }
    return () => {
      alive = false
    }
  }, [isOpen, source, videoModelMode, describing, storyShape])

  // A square source resolves to a Square orientation, which v5 can't render — coerce rather
  // than letting the user confirm into a 400. Also covers switching v4 → v5 with Square picked.
  useEffect(() => {
    if (!ltx) return
    const allowed = normalizeAspectForVersion(orientation, version)
    if (allowed !== orientation) setOrientation(allowed)
  }, [ltx, version, orientation])

  if (!shouldRender) return null

  const thumb = source?.url
    ? isUrl(source.url)
      ? source.url
      : `data:image/jpeg;base64,${source.url}`
    : null

  const gemCost = getVideoGemCost({
    videoQuality,
    videoVersion: version,
    audioEnabled,
    // A described scene makes its own picture first, as the Video tab does (Flame costs more for it).
    model: describing ? source.model : undefined,
    hasReferenceImage: !describing,
    videoLength,
    videoResolution,
    videoFps,
  })
  const dialogueLimits = ltxDialogueLimits(videoLength)
  const dialogueWords = dialogueWordCount(dialogue)
  const dialogueOverLimit = dialogueWords > dialogueLimits.words
  const orientationOptions = ltx
    ? [
        { value: 'Portrait', label: t('portrait') },
        { value: 'Wide', label: t('wide') },
      ]
    : [
        { value: 'Square', label: t('square') },
        { value: 'Portrait', label: t('portrait') },
        { value: 'Wide', label: t('wide') },
      ]

  const handleAnimate = () => {
    // Prompt mode with an empty box degrades to auto so we never send a blank
    // motion prompt (matches the old videoPrompt='' behavior).
    const effectiveMode =
      animateMode === 'prompt' && motionPrompt.trim() ? 'prompt' : 'auto'
    onConfirm?.({
      videoVersion: version,
      // The MODE travels with the result so committing an animate does not force the Video tab
      // off Auto — see the mode/version branch in Create.jsx.
      videoModelMode: modelMode,
      animateMode: effectiveMode,
      motionPrompt: effectiveMode === 'prompt' ? motionPrompt.trim() : '',
      orientation: normalizeAspectForVersion(orientation, version),
      // v5 has no quality tier and always renders audio — the backend normalises both, so send
      // the values it will actually use rather than a stale draft.
      videoQuality: ltx ? 'Standard' : videoQuality,
      audioEnabled: ltx ? true : audioEnabled,
      dialogue: ltx ? dialogue.trim() : '',
      dialogueSpeaker: ltx ? dialogueSpeaker : LTX_DIALOGUE_SPEAKER_DEFAULT,
      dialogueTone: ltx ? dialogueTone : LTX_DIALOGUE_TONE_DEFAULT,
      dialogueAccent: ltx ? dialogueAccent : LTX_DIALOGUE_ACCENT_DEFAULT,
      // Camera is only sent when it was actually offered — safe mode ignores it anyway.
      cameraMove: ltxExtras ? cameraMove : '',
      // v5-only settings. Sent as-is for v5; omitted for v4, whose caller keeps its own values.
      ...(ltx ? { videoLength, videoFps, videoResolution } : {}),
    })
    closeModal()
  }

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal()
        }}
        className={`fixed z-[110] top-0 left-0 w-full h-full bg-black/70 p-3 flex justify-center items-center transition-opacity duration-200 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className={`bg-[var(--middle-bg-color)] max-h-[85vh] w-full max-w-md flex flex-col overflow-hidden min-h-0 rounded-2xl border border-white/10 shadow-2xl p-5 text-gray-100 space-y-3 relative transform transition-all duration-200 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
          }`}
        >
          <header>
            <h2 className="flex items-center gap-2 text-2xl font-extrabold text-shadow">
              <FaVideo className="text-[var(--main-bg-color)]" />
              {/* Opened from a story's New scene: the result joins that story as its next scene (ClipChainCard) */}
              {source?.scene ? t('clips_new_scene') : t('animate')}
            </h2>
            <h3 className="mt-1 text-sm font-medium text-gray-400">
              {source?.scene
                ? t('clips_scene_modal_hint')
                : t('bringYourImageToLife')}
            </h3>
            <button
              onClick={closeModal}
              aria-label={t('close')}
              className="absolute right-3 top-3 text-white/60 transition-colors hover:text-white"
              style={{ fontSize: '1.4rem' }}
            >
              <FaTimes />
            </button>
          </header>
          <hr className="border-gray-800" />

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            {/* A described scene: its words lead, as the picture leads an animate. They make the scene's picture
                and its motion, like the Video tab's prompt. */}
            {describing && (
              <div>
                <label
                  htmlFor="scenePrompt"
                  className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-white"
                >
                  <FaRunning size={12} />
                  {t('clips_scene_prompt_label')}
                </label>
                <textarea
                  id="scenePrompt"
                  value={motionPrompt}
                  onChange={(e) => setMotionPrompt(e.target.value)}
                  placeholder={t('clips_scene_prompt_placeholder')}
                  maxLength={400}
                  autoFocus
                  className="w-full h-28 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                />
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('clips_scene_prompt_hint')}
                </p>
              </div>
            )}
            {thumb && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={thumb}
                alt=""
                className="mx-auto max-h-40 w-auto rounded-xl object-contain"
              />
            )}

            {/* Video model — same switcher as the Video tab, so an animate started from a feed
                card doesn't silently use a different model than the panel shows. */}
            <div>
              <div className="flex items-center gap-2">
                {/* Same inline primary-colour pill as the Video tab, on the label rather than
                    on the model row — no fixed w-24 here so it isn't clipped. */}
                <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0">
                  <FaMicrochip className="text-white" size={12} />
                  {t('videoModel')}
                  <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {t('new')}
                  </span>
                </span>
                <Segmented
                  // The MODE is what is selected here, not the resolved version — so Auto stays
                  // visibly Auto after a dialogue line flips the render to the voice model.
                  value={modelMode}
                  onChange={pickVideoModel}
                  options={[
                    { value: VIDEO_MODEL_AUTO, label: t('videoModelAuto') },
                    { value: VIDEO_V4, label: 'v4' },
                    { value: VIDEO_V5, label: 'v4-voice' },
                  ]}
                />
              </div>
              {/* Deliberately NOT conditional. One sentence that states the whole model
                  story — what Auto does, and what the voice model trades — so it reads the
                  same however the switcher is set. */}
              <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                {t('videoModelHint')}
              </p>
            </div>

            {/* Motion FIRST, then Dialogue — the same order as the Video tab's InputsVideo.
                It used to be the other way round here ("dialogue leads, it's the headline
                feature"), which meant the two surfaces presented two identically-styled h-20
                textareas in OPPOSITE orders. A user who learned "the top box is motion" on the
                Video tab typed their motion description into the dialogue box here, and the
                character then read it aloud. Whatever leads, both surfaces must agree. */}
            {!describing && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                    <FaRunning className="text-white" size={12} />
                    {t('motion')}
                  </span>
                  <Segmented
                    value={animateMode}
                    onChange={setAnimateMode}
                    options={[
                      { value: 'auto', label: 'Auto' },
                      { value: 'prompt', label: t('prompt') },
                    ]}
                  />
                </div>
                {/* Rendered in BOTH modes, disabled under Auto rather than unmounted — same fix
                  as the Video tab. Unmounted, the next box down was Dialogue, leaving the only
                  textarea under a "Motion" heading as the one whose contents get SPOKEN. */}
                <textarea
                  id="motionPrompt"
                  value={animateMode === 'prompt' ? motionPrompt : ''}
                  onChange={(e) => setMotionPrompt(e.target.value)}
                  placeholder={
                    animateMode === 'prompt'
                      ? t('describeTheMotion')
                      : t('motionAutoPlaceholder')
                  }
                  disabled={animateMode !== 'prompt'}
                  aria-label={t('motion')}
                  maxLength={180}
                  className="w-full h-20 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('autoOrPromptHint')}
                </p>
              </div>
            )}

            {/* Dialogue. The box takes the WORDS SPOKEN, verbatim: the backend wraps it as
                `the woman says "<line>"`, so "make her sound surprised" would be read aloud as
                those words. Delivery belongs in the motion prompt, which the hint says.
                Over-long lines are clamped by the backend on a word boundary, never rejected. */}
            {/* Shown on Auto even while v4 is resolved — otherwise Auto is unreachable here:
                Auto starts on v4, and this box is the only way to type the line that selects
                the voice model. Same reasoning as the Video tab's dialogueSection. */}
            {(ltx || modelMode === VIDEO_MODEL_AUTO) && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5">
                    <FaMicrophoneAlt className="text-white" size={12} />
                    {t('dialogue')}
                  </span>
                </div>
                <textarea
                  id="dialogue"
                  value={dialogue}
                  onChange={(e) => setDialogue(e.target.value)}
                  placeholder={t('dialoguePlaceholder')}
                  maxLength={dialogueLimits.chars}
                  className="w-full h-20 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                />
                {/* Voice settings and the counter on one compact row — everything about HOW
                    the line is said sits behind one pill. Shared with the Video tab so the two
                    surfaces cannot drift apart. */}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <VoiceSettings
                    className="min-w-0"
                    dialogueSpeaker={dialogueSpeaker}
                    setDialogueSpeaker={setDialogueSpeaker}
                    dialogueTone={dialogueTone}
                    setDialogueTone={setDialogueTone}
                    dialogueAccent={dialogueAccent}
                    setDialogueAccent={setDialogueAccent}
                  />
                  <span
                    className={`text-xs tabular-nums shrink-0 ${
                      dialogueOverLimit ? 'text-amber-400' : 'text-gray-400'
                    }`}
                  >
                    {dialogueWords}/{dialogueLimits.words}
                  </span>
                </div>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {dialogueOverLimit
                    ? t('dialogueOverLimit', { count: dialogueLimits.words })
                    : t('dialogueHint')}
                </p>
                {/* The modal seeds this box from the last-used line, so it can arrive
                    pre-filled from a previous animate — and a non-empty line silently moves
                    Auto onto the voice model AND changes the gem total while the switcher
                    above still reads "Auto". Say so, and offer the one tap back to v4. */}
                {isAutoVoice(modelMode, dialogue) && (
                  <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2">
                    <FaMicrophoneAlt
                      className="text-amber-400 mt-0.5 shrink-0"
                      size={11}
                    />
                    <p className="text-xs text-amber-200 leading-relaxed flex-1">
                      {t('autoVoiceNotice')}
                    </p>
                    <button
                      type="button"
                      onClick={() => setDialogue('')}
                      className="text-xs font-semibold text-amber-200 underline underline-offset-2 hover:text-white shrink-0"
                    >
                      {t('clear')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Orientation. v5 renders portrait or landscape only — no Square. */}
            <div className="flex items-center gap-2">
              <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                <FaExpandAlt className="text-white" size={12} />
                {t('imageSize')}
              </span>
              {/* A story's scene keeps the story's shape: another one couldn't join it */}
              {storyShape ? (
                <span
                  className={`text-xs ${shapeClash ? 'text-amber-300' : 'text-gray-300'}`}
                >
                  {shapeClash
                    ? t('clips_scene_shape_clash')
                    : t('clips_scene_shape_locked', {
                        shape:
                          orientationOptions.find(
                            (o) => o.value === orientation,
                          )?.label ?? orientation,
                      })}
                </span>
              ) : (
                <Segmented
                  value={orientation}
                  onChange={setOrientation}
                  options={orientationOptions}
                />
              )}
            </div>

            {/* Clip length / frame rate / resolution (v5). Parity with the Video tab: the modal
                is a full alternative entry point, so animating from here must not silently use
                whatever those were last set to without showing them. The gem total above updates
                live — getVideoGemCost is fed all three. */}
            {ltx && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                    <FaClock className="text-white" size={12} />
                    {t('clipLength')}
                  </span>
                  <Segmented
                    value={videoLength}
                    onChange={setVideoLength}
                    options={LTX_LENGTHS.map((l) => ({
                      value: l.value,
                      label: `${l.value}s`,
                    }))}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                    <FaTachometerAlt className="text-white" size={12} />
                    {t('frameRate')}
                  </span>
                  <Segmented
                    value={videoFps}
                    onChange={setVideoFps}
                    options={LTX_FPS_OPTIONS.map((f) => ({
                      value: f,
                      label: `${f} ${t('fps')}`,
                    }))}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                    <FaExpandAlt className="text-white" size={12} />
                    {t('videoResolution')}
                  </span>
                  <Segmented
                    value={videoResolution}
                    onChange={setVideoResolution}
                    options={LTX_RESOLUTIONS.map((r) => ({
                      value: r.value,
                      label: t(r.labelKey),
                    }))}
                  />
                </div>
              </>
            )}

            {/* Camera motion (v5). "None" is right for most renders, so it sits below the
                primary choices — same demotion as the Video tab's More settings. */}
            {ltxExtras && (
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                    <FaVideo className="text-white" size={12} />
                    {t('cameraMotion')}
                  </span>
                  <Segmented
                    value={cameraMove}
                    onChange={setCameraMove}
                    options={LTX_CAMERA_MOVES.map((m) => ({
                      value: m.value,
                      label: t(m.labelKey),
                    }))}
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {cameraMove
                    ? t('cameraMotionHintLora')
                    : t('cameraMotionHint')}
                </p>
              </div>
            )}

            {/* Quality (no plan-lock UI: video is Pro-only overall). v5 has one fixed spec. */}
            {!ltx && (
              <div className="flex items-center gap-2">
                <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                  {t('quality')}
                </span>
                <Segmented
                  value={videoQuality}
                  onChange={setVideoQuality}
                  options={[
                    { value: 'Standard', label: 'Standard' },
                    { value: 'High', label: 'High' },
                  ]}
                />
              </div>
            )}

            {/* Audio is a v4 control. v5 renders its soundtrack in the same job, always, at no
                extra cost — nothing to toggle, and a row that only ever reads "Included" reads
                as a switch that does nothing. The v5 model hint above carries the fact. */}
            {!ltx && (
              <div className="flex items-center gap-2">
                <span className="text-white text-xs font-semibold flex items-center gap-1.5 flex-1">
                  <HiSpeakerphone className="text-white" size={12} />
                  {t('audio')}
                  {audioEnabled && (
                    <span className="flex items-center text-white">
                      <FaGem size={9} />
                      <span className="font-semibold text-[10px] ml-0.5">
                        +3
                      </span>
                    </span>
                  )}
                </span>
                <div
                  role="switch"
                  aria-checked={audioEnabled}
                  onClick={() => setAudioEnabled((v) => !v)}
                  className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                    audioEnabled
                      ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                      : 'bg-[var(--foreground-bg-color)]'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                      audioEnabled
                        ? 'translate-x-6 bg-white'
                        : 'translate-x-0 bg-gray-500'
                    }`}
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleAnimate}
            disabled={shapeClash || (describing && !motionPrompt.trim())}
            data-track={
              source?.scene
                ? describing
                  ? 'generate.clips_scene_describe_create'
                  : 'generate.clips_scene_create'
                : undefined
            }
            className="disabled:opacity-50 disabled:cursor-not-allowed shrink-0 w-full h-12 rounded-2xl bg-[var(--main-bg-color)] text-white font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <FaVideo size={16} />
            {source?.scene ? t('clips_add_scene') : t('animate')}
            <span className="inline-flex items-center ml-1 text-white/90">
              <FaGem size={11} className="mr-0.5" />
              <span className="font-semibold text-sm">{gemCost}</span>
            </span>
          </button>
        </div>
      </div>
    </Portal>
  )
}
